import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';

const RealTimeUpdates = () => {
  const [updates, setUpdates] = useState([]);
  const [connectionStatus, setConnectionStatus] = useState('connected');
  const [lastUpdate, setLastUpdate] = useState(new Date());

  useEffect(() => {
    // Simulate real-time updates
    const generateUpdate = () => {
      const updateTypes = [
        {
          type: 'fuel',
          icon: 'Fuel',
          color: 'text-accent',
          templates: [
            'MV Atlantic Voyager fuel consumption: {value} MT/day',
            'MV Pacific Trader bunker level: {value}% remaining',
            'MV Mediterranean Star fuel efficiency: {value} g CO₂/tkm'
          ]
        },
        {
          type: 'position',
          icon: 'Navigation',
          color: 'text-primary',
          templates: [
            'MV Atlantic Voyager position updated: {value}',
            'MV Pacific Trader departed {value}',
            'MV Mediterranean Star ETA updated: {value}'
          ]
        },
        {
          type: 'emissions',
          icon: 'Zap',
          color: 'text-warning',
          templates: [
            'Fleet CO₂ emissions: {value} kg/hour',
            'Daily emissions target: {value}% achieved',
            'ETS exposure updated: €{value}K'
          ]
        },
        {
          type: 'trading',
          icon: 'TrendingUp',
          color: 'text-success',
          templates: [
            'EUA price update: €{value}/tonne',
            'Trading volume: {value} allowances',
            'Market volatility: {value}% change'
          ]
        },
        {
          type: 'compliance',
          icon: 'Shield',
          color: 'text-error',
          templates: [
            'Compliance check: {value} vessels reviewed',
            'CII rating update: {value} vessels affected',
            'Regulatory deadline: {value} days remaining'
          ]
        }
      ];

      const randomType = updateTypes?.[Math.floor(Math.random() * updateTypes?.length)];
      const randomTemplate = randomType?.templates?.[Math.floor(Math.random() * randomType?.templates?.length)];
      
      let value;
      switch (randomType?.type) {
        case 'fuel':
          value = (Math.random() * 100 + 50)?.toFixed(1);
          break;
        case 'position':
          value = ['London Port', 'Hamburg', 'Rotterdam', 'Antwerp']?.[Math.floor(Math.random() * 4)];
          break;
        case 'emissions':
          value = (Math.random() * 1000 + 500)?.toFixed(0);
          break;
        case 'trading':
          value = (Math.random() * 50 + 70)?.toFixed(2);
          break;
        case 'compliance':
          value = Math.floor(Math.random() * 24 + 1);
          break;
        default:
          value = Math.floor(Math.random() * 100);
      }

      return {
        id: Date.now() + Math.random(),
        type: randomType?.type,
        icon: randomType?.icon,
        color: randomType?.color,
        message: randomTemplate?.replace('{value}', value),
        timestamp: new Date(),
        priority: Math.random() > 0.8 ? 'high' : 'normal'
      };
    };

    // Initial updates
    const initialUpdates = Array.from({ length: 5 }, generateUpdate);
    setUpdates(initialUpdates);

    // Simulate real-time updates
    const interval = setInterval(() => {
      const newUpdate = generateUpdate();
      setUpdates(prev => [newUpdate, ...prev?.slice(0, 9)]); // Keep last 10 updates
      setLastUpdate(new Date());
    }, 3000 + Math.random() * 4000); // Random interval between 3-7 seconds

    // Simulate connection status changes
    const connectionInterval = setInterval(() => {
      const statuses = ['connected', 'connecting', 'connected', 'connected']; // Mostly connected
      const randomStatus = statuses?.[Math.floor(Math.random() * statuses?.length)];
      setConnectionStatus(randomStatus);
    }, 15000);

    return () => {
      clearInterval(interval);
      clearInterval(connectionInterval);
    };
  }, []);

  const getConnectionColor = (status) => {
    switch (status) {
      case 'connected': return 'text-success';
      case 'connecting': return 'text-warning';
      case 'disconnected': return 'text-error';
      default: return 'text-muted-foreground';
    }
  };

  const getConnectionIcon = (status) => {
    switch (status) {
      case 'connected': return 'Wifi';
      case 'connecting': return 'Loader2';
      case 'disconnected': return 'WifiOff';
      default: return 'Wifi';
    }
  };

  const getPriorityStyles = (priority) => {
    return priority === 'high' ?'bg-warning/10 border-l-warning' :'bg-muted/30 border-l-border';
  };

  const formatTime = (date) => {
    return date?.toLocaleTimeString('en-US', {
      hour12: false,
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  return (
    <div className="maritime-card">
      <div className="p-4 border-b border-border">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <h3 className="text-lg font-semibold text-foreground">Live Updates</h3>
            <div className={`flex items-center space-x-2 text-sm ${getConnectionColor(connectionStatus)}`}>
              <Icon 
                name={getConnectionIcon(connectionStatus)} 
                size={16} 
                strokeWidth={2}
                className={connectionStatus === 'connecting' ? 'animate-spin' : ''}
              />
              <span className="capitalize">{connectionStatus}</span>
            </div>
          </div>
          
          <div className="text-xs text-muted-foreground">
            Last update: {formatTime(lastUpdate)}
          </div>
        </div>
      </div>
      <div className="max-h-96 overflow-y-auto">
        {updates?.length === 0 ? (
          <div className="p-6 text-center">
            <Icon name="Loader2" size={24} strokeWidth={2} className="animate-spin mx-auto mb-2 text-muted-foreground" />
            <p className="text-sm text-muted-foreground">Connecting to live data feeds...</p>
          </div>
        ) : (
          <div className="divide-y divide-border">
            {updates?.map((update) => (
              <div
                key={update?.id}
                className={`p-4 border-l-4 transition-maritime ${getPriorityStyles(update?.priority)}`}
              >
                <div className="flex items-start space-x-3">
                  <Icon 
                    name={update?.icon} 
                    size={16} 
                    strokeWidth={2}
                    className={`mt-0.5 ${update?.color}`}
                  />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-foreground">
                      {update?.message}
                    </p>
                    <div className="flex items-center justify-between mt-2">
                      <span className="text-xs text-muted-foreground capitalize">
                        {update?.type} update
                      </span>
                      <span className="text-xs text-muted-foreground">
                        {formatTime(update?.timestamp)}
                      </span>
                    </div>
                  </div>
                  {update?.priority === 'high' && (
                    <div className="flex-shrink-0">
                      <div className="w-2 h-2 bg-warning rounded-full animate-pulse" />
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      <div className="p-4 border-t border-border">
        <div className="flex items-center justify-between text-xs text-muted-foreground">
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-2">
              <div className="w-2 h-2 bg-success rounded-full" />
              <span>Normal</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-2 h-2 bg-warning rounded-full animate-pulse" />
              <span>High Priority</span>
            </div>
          </div>
          <div>
            {updates?.length} recent updates
          </div>
        </div>
      </div>
    </div>
  );
};

export default RealTimeUpdates;